import { useState } from "react";
import type { MathpixResult } from "#/lib/mathpix";
import {
	recognizeWorkingOut,
	saveQuestion,
	uploadImage,
} from "#/server/questions";
import { CanvasStage } from "./CanvasStage";
import { ReviewStage } from "./ReviewStage";
import { UploadStage } from "./UploadStage";

type Stage =
	| { kind: "upload" }
	| { kind: "draw"; questionImageKey: string; questionImageUrl: string }
	| {
			kind: "review";
			questionImageKey: string;
			questionImageUrl: string;
			workingOutImageKey: string;
			workingOutImageUrl: string;
			mathpix: MathpixResult;
	  }
	| {
			kind: "saving";
			questionImageKey: string;
			questionImageUrl: string;
			workingOutImageKey: string;
			workingOutImageUrl: string;
			mathpix: MathpixResult;
			finalLatex: string;
	  };

export function QuestionMaker() {
	const [stage, setStage] = useState<Stage>({ kind: "upload" });
	const [questionId, setQuestionId] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [showQuestion, setShowQuestion] = useState(true);

	function reportError(e: unknown) {
		setError(e instanceof Error ? e.message : String(e));
	}

	async function handleQuestionFile(file: File) {
		setError(null);
		setBusy(true);
		try {
			const fd = new FormData();
			fd.set("kind", "question");
			fd.set("file", file);
			const { key } = await uploadImage({ data: fd });
			const url = URL.createObjectURL(file);
			setStage({
				kind: "draw",
				questionImageKey: key,
				questionImageUrl: url,
			});
		} catch (e) {
			reportError(e);
		} finally {
			setBusy(false);
		}
	}

	async function handleDone(blob: Blob) {
		if (stage.kind !== "draw") return;
		setError(null);
		setBusy(true);
		try {
			const file = new File([blob], "working.png", { type: "image/png" });
			const fd = new FormData();
			fd.set("kind", "workingOut");
			fd.set("file", file);
			const { key } = await uploadImage({ data: fd });
			const mathpix = await recognizeWorkingOut({ data: { key } });
			const url = URL.createObjectURL(blob);
			setStage({
				kind: "review",
				questionImageKey: stage.questionImageKey,
				questionImageUrl: stage.questionImageUrl,
				workingOutImageKey: key,
				workingOutImageUrl: url,
				mathpix,
			});
		} catch (e) {
			reportError(e);
		} finally {
			setBusy(false);
		}
	}

	async function handleRetryRecognize() {
		if (stage.kind !== "review") return;
		setError(null);
		setBusy(true);
		try {
			const mathpix = await recognizeWorkingOut({
				data: { key: stage.workingOutImageKey },
			});
			setStage({ ...stage, mathpix });
		} catch (e) {
			reportError(e);
		} finally {
			setBusy(false);
		}
	}

	function handleBackToCanvas() {
		if (stage.kind !== "review") return;
		setStage({
			kind: "draw",
			questionImageKey: stage.questionImageKey,
			questionImageUrl: stage.questionImageUrl,
		});
	}

	async function handleComplete(finalLatex: string) {
		if (stage.kind !== "review") return;
		setError(null);
		setBusy(true);
		const reviewStage = stage;
		setStage({ ...reviewStage, kind: "saving", finalLatex });
		try {
			await saveQuestion({
				data: {
					questionKey: reviewStage.questionImageKey,
					workingOutKey: reviewStage.workingOutImageKey,
					finalLatex,
					mathpixRawJson: reviewStage.mathpix.rawJson,
					mathpixRequestId: reviewStage.mathpix.requestId,
					confidence: reviewStage.mathpix.confidence,
					isHandwritten: reviewStage.mathpix.isHandwritten,
				},
			});
			URL.revokeObjectURL(reviewStage.questionImageUrl);
			URL.revokeObjectURL(reviewStage.workingOutImageUrl);
			setQuestionId((n) => n + 1);
			setStage({ kind: "upload" });
		} catch (e) {
			reportError(e);
			setStage(reviewStage);
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="h-screen w-full flex flex-col overflow-hidden">
			<header className="px-4 py-3 border-b flex items-center gap-3 bg-white shrink-0">
				<h1 className="text-lg font-semibold">Question Maker</h1>
				<span className="text-xs text-zinc-500">stage: {stage.kind}</span>
				{busy && <span className="text-xs text-blue-600">working…</span>}
				{stage.kind === "draw" && (
					<button
						type="button"
						onClick={() => setShowQuestion((v) => !v)}
						className="ml-2 text-xs px-2 py-1 rounded border bg-white text-zinc-700 hover:bg-zinc-50"
						aria-pressed={showQuestion}
					>
						{showQuestion ? "Hide question" : "Show question"}
					</button>
				)}
				{error && (
					<span className="ml-auto text-sm text-red-600 truncate max-w-xl">
						{error}
					</span>
				)}
			</header>
			<main className="flex-1 relative min-h-0">
				{stage.kind === "upload" && <UploadStage onFile={handleQuestionFile} />}

				{stage.kind !== "upload" && (
					<div
						key={questionId}
						className="absolute inset-0"
						style={{ display: stage.kind === "draw" ? "block" : "none" }}
					>
						<CanvasStage
							questionImageUrl={stage.questionImageUrl}
							showQuestion={showQuestion}
							onDone={handleDone}
						/>
					</div>
				)}

				{stage.kind === "review" && (
					<ReviewStage
						questionImageUrl={stage.questionImageUrl}
						workingOutImageUrl={stage.workingOutImageUrl}
						mathpix={stage.mathpix}
						onComplete={handleComplete}
						onBack={handleBackToCanvas}
						onRetryRecognize={handleRetryRecognize}
					/>
				)}

				{stage.kind === "saving" && (
					<div className="absolute inset-0 flex items-center justify-center bg-white/80">
						<p className="text-zinc-700">Saving question…</p>
					</div>
				)}
			</main>
		</div>
	);
}
